import { fetchApiData, postApiData } from '@/helpers/api'

/**
 * Config Settings Service
 * Handles organization-level configuration settings API calls to bits-backend
 */

// Extract the meaningful server error message from an API error so validation
// errors (e.g. "documentExpirationTimeMinutes is required and must be a positive integer")
// are shown to the user instead of being replaced with a generic message.
const getServerMessage = (error, fallback) => {
  const raw = (error && error.message) || ''
  if (!raw) return fallback
  const apiMatch = raw.match(/API Error:[\s\S]* - (.+)$/)
  return apiMatch ? apiMatch[1] : fallback
}

const isMissingConfigError = (error) => {
  const raw = (error && error.message) || ''
  return /not found|no config|does not exist|404/i.test(raw)
}

class ConfigService {
  /**
     * Get the public branding configuration for the organization selected by
     * the deployed application. This endpoint is intentionally unauthenticated
     * so branding can be resolved before the app mounts.
     * @returns {Promise<Object|null>} Public organization configuration
     */
  async getPublicConfig () {
    try {
      return await fetchApiData('api/public/organization/config')
    } catch (error) {
      if (isMissingConfigError(error)) {
        return null
      }
      console.error('Error fetching public organization config:', error)
      throw new Error(getServerMessage(error, 'Failed to fetch public organization configuration'))
    }
  }

  /**
     * Get the configuration settings for an organization
     * @param {string} organizationId - Organization ID
     * @returns {Promise<Object>} Organization configuration settings
     */
  async getConfig (organizationId) {
    try {
      const response = await fetchApiData(`api/organization/${organizationId}/config`)
      return response
    } catch (error) {
      if (isMissingConfigError(error)) {
        return null
      }
      console.error('Error fetching organization config:', error)
      throw new Error(getServerMessage(error, 'Failed to fetch configuration settings'))
    }
  }

  /**
     * Update the configuration settings for an organization
     * @param {string} organizationId - Organization ID
     * @param {Object} config - Configuration settings to save
     * @returns {Promise<Object>} Update result
     */
  async updateConfig (organizationId, config) {
    try {
      const response = await postApiData(`api/organization/${organizationId}/config`, config)
      return response
    } catch (error) {
      console.error('Error updating organization config:', error)
      throw new Error(getServerMessage(error, 'Failed to save configuration settings'))
    }
  }
}

// Export singleton instance
export default new ConfigService()
